<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { PiInputField, PiMessage, PiSpinner } from '@pappers-fr/pappers-ui';
import { useFoldersStore } from '@/stores/folders';
import { useWorkspaceApi } from '@/composables/useWorkspaceApi';
import { useAuth } from '@/composables/useAuth';
import FolderCard from './FolderCard.vue';

const foldersStore = useFoldersStore();
const { fetchFolders } = useWorkspaceApi();

const filteredFolders = computed(() => {
	if (!foldersStore.searchQuery) return foldersStore.folders;
	const query = foldersStore.searchQuery.toLowerCase();
	return foldersStore.folders.filter((f) => f.name.toLowerCase().includes(query));
});

const loadFolders = async () => {
	foldersStore.isLoading = true;
	foldersStore.error = null;
	try {
		const { organizationId } = useAuth();
		if (!organizationId.value) throw new Error('Organisation non trouvée');
		const folders = await fetchFolders(organizationId.value);
		foldersStore.setFolders(folders);
	} catch (e: any) {
		foldersStore.error = e.message;
	} finally {
		foldersStore.isLoading = false;
	}
};

onMounted(loadFolders);
</script>

<template>
	<div class="flex flex-col h-full">
		<!-- Header -->
		<div class="flex items-center justify-between p-3 border-b border-neutral-100">
			<h2 class="text-sm font-semibold text-neutral-800">Mes dossiers</h2>
		</div>

		<!-- Recherche -->
		<div class="p-3">
			<PiInputField
				:model-value="foldersStore.searchQuery"
				placeholder="Rechercher un dossier..."
				size="sm"
				@update:model-value="foldersStore.searchQuery = $event"
			/>
		</div>

		<!-- Loading -->
		<div v-if="foldersStore.isLoading" class="flex items-center justify-center p-8">
			<PiSpinner />
		</div>

		<!-- Erreur -->
		<PiMessage
			v-else-if="foldersStore.error"
			type="danger"
			:label="foldersStore.error"
			link-label="Réessayer"
			size="sm"
			has-close-button
			@link-alert="loadFolders"
			@close-alert="foldersStore.error = null"
		/>

		<!-- Liste vide -->
		<div v-else-if="filteredFolders.length === 0" class="flex flex-col items-center p-8 text-center">
			<p class="text-sm text-neutral-500">Aucun dossier trouvé</p>
		</div>

		<!-- Liste des dossiers -->
		<div v-else class="flex-1 overflow-y-auto px-3 pb-3">
			<FolderCard
				v-for="folder in filteredFolders"
				:key="folder.id"
				:folder="folder"
				@click="foldersStore.selectFolder(folder)"
			/>
		</div>
	</div>
</template>
